/** @jsx jsx */
import { jsx } from '@emotion/react';

import { type Specialization_Specialization as S12n } from '__generated__/graphql-types-do-not-use';

import { Grid, PageGridContainer, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { useSkillEntity } from 'bundles/browse-common/contexts/SkillEntityContext';
import { useOnboardingV3Context } from 'bundles/onboarding-common/providers/OnboardingV3Provider';
import PathwaysCarouselCollections from 'bundles/page-config-common/components/shared/PathwaysCarouselCollections';
import { usePageConfigComponentMetadata } from 'bundles/page-config-common/contexts/PageConfigComponentMetadataContext';
import { useS12nTargetDegrees } from 'bundles/page-config-common/contexts/S12nTargetDegreesContext';
import type {
  SpotlightCollectionProductCard,
  SpotlightCollectionWithSecondaryCta,
} from 'bundles/page-config-common/types/spotlight-collection';
import { formatRoleAndSkillExperimental } from 'bundles/page-config-common/utils/getEditCareerGoalCopy';
import { getRecommendedPathwayProps } from 'bundles/page-config-common/utils/getRecommendedPathwayProps';
import { isPathwaysForAllEnabled } from 'bundles/page-config-common/utils/isPathwaysForAllEnabled';
import { useIntl } from 'bundles/page/lib/useIntl';
import { formatProductTypeWithCourseCount } from 'bundles/product-card/components/utils/productCardV2Utils';
import { ProductCardV2Placeholder } from 'bundles/search-common/components/search-cards/Placeholder';
import GeneralCollection from 'bundles/unified-common/components/GeneralCollection';
import RecommendedPathwayComponent from 'bundles/unified-common/components/recommended-pathway/RecommendedPathwayComponent';
import RecommendedRoleHeader from 'bundles/unified-common/components/recommended-pathway/RecommendedRoleHeader';
import RecommendedRoleSecondaryCta from 'bundles/unified-common/components/recommended-pathway/RecommendedRoleSecondaryCta';
import isRdpSlug from 'bundles/unified-common/utils/isRdpSlug';

type Props = {
  showSingleCardDisplay: boolean;
  recommendationCollection: SpotlightCollectionWithSecondaryCta;
  productCards: SpotlightCollectionProductCard[];
  twoColumnLayout?: boolean;
  hideCoachButton?: boolean;
};

const SimpleSpotlightCollectionModuleRenderer = (props: Props) => {
  const { showSingleCardDisplay, recommendationCollection, productCards, twoColumnLayout, hideCoachButton } = props;
  const pageConfigComponentMetadata = usePageConfigComponentMetadata();
  const pageConfigComponentTrackingData = {
    id: pageConfigComponentMetadata?.id,
    index: pageConfigComponentMetadata?.index ?? -1,
    name: pageConfigComponentMetadata?.name ?? 'unknown',
    recommender: pageConfigComponentMetadata?.recommender,
  };

  const enablePathwaysForAll = isPathwaysForAllEnabled();
  const trackV3 = useTracker();
  const s12nTargetDegrees = useS12nTargetDegrees();
  const intl = useIntl();

  const { getShouldShowOnboardingV3 } = useOnboardingV3Context();

  const pathwaysHash = recommendationCollection.recommenderConfigurationHash;
  const pathwaysContext = recommendationCollection.context;
  const pathways: S12n[] = productCards
    .map((pathway) => {
      if (pathway.productTypeAttributes.__typename === 'ProductCard_Specialization') {
        return pathway.productTypeAttributes.canonical.latestSpecialization as S12n;
      } else {
        return null;
      }
    })
    .filter(Boolean);

  const skillRole = {
    slug: recommendationCollection.additionalInput.role_id,
    name: recommendationCollection.additionalInput.role_name,
  };

  const hash = recommendationCollection.recommenderConfigurationHash;
  const context = recommendationCollection.context;
  const index = 0;
  const numDisplayedRows: number = showSingleCardDisplay ? 1 : 2;
  const onboardingEditLink = recommendationCollection.secondaryCta;

  const recommenderId = recommendationCollection.id || '';
  const roleUrl = isRdpSlug(skillRole.slug)
    ? `/career-academy/roles/${skillRole.slug}?recommenderId=${recommenderId}`
    : '';

  const role = {
    name: skillRole.name,
    slug: skillRole.slug,
    url: roleUrl,
  };

  const trackingData = {
    productCard: {
      recommender: { hash: pathwaysHash, context: pathwaysContext },
      pageConfigComponent: pageConfigComponentTrackingData,
    },
    carousel: {
      name: skillRole.slug?.replace(/-/g, '_') ?? 'unknown',
      recommender: {
        hash: hash || 'hash',
        context,
      },
      id: recommenderId,
      section: index,
    },
  };

  const maybeSkillEntity = useSkillEntity();

  const { formattedRoleAndSkill, formattedRoleAndSkillLabel } = formatRoleAndSkillExperimental({
    recommenderId,
    roleName: maybeSkillEntity ? recommendationCollection.description : skillRole.name,
    roleUrl,
    skillOrCollectionTitle: maybeSkillEntity ? maybeSkillEntity.skill.skillName : recommendationCollection.description,
    onlyCollectionTitle: recommendationCollection.description,
    onRoleLinkClick: () => {
      trackV3('click_button', {
        button: {
          linkName: 'lihp_rdp_link',
          name: 'role_info',
          linkURL: role.url,
          pageConfigComponent: pageConfigComponentTrackingData,
        },
        carousel: trackingData.carousel,
      });
    },
  });

  const collectionTitle = (
    <RecommendedRoleHeader
      title={
        <Typography2 component="h2" variant="subtitleLarge" aria-label={formattedRoleAndSkillLabel}>
          {formattedRoleAndSkill}
        </Typography2>
      }
      onboardingText={onboardingEditLink?.ctaTitle}
      onboardingQuestion={onboardingEditLink?.ctaUrl.includes('skill-select') ? 'skill-select' : 'learning-goal'}
      trackingLinkName={onboardingEditLink?.ctaTitle ?? ''}
      hideHeaderLink={!enablePathwaysForAll}
      shouldShowOnboardingV3={getShouldShowOnboardingV3()}
    />
  );

  const content = enablePathwaysForAll ? (
    <PathwaysCarouselCollections
      collections={[{ id: 'default', items: pathways }]}
      componentProps={(s12n, itemIndex) => {
        return getRecommendedPathwayProps(
          trackingData,
          s12n,
          itemIndex,
          pathways,
          role,
          index,
          s12nTargetDegrees?.[s12n.id],
          'subtitleMedium',
          undefined,
          undefined,
          formatProductTypeWithCourseCount(intl, s12n.productVariant, s12n.courses.length)
        );
      }}
      title={collectionTitle}
      trackingData={trackingData}
      roleDetailsCta={<RecommendedRoleSecondaryCta role={role} carouselTrackingData={trackingData.carousel} />}
      cardLoadingComponent={<ProductCardV2Placeholder />}
    />
  ) : (
    <Grid item xs={12}>
      <GeneralCollection
        disablePadding={true}
        key={role.name}
        title={collectionTitle}
        Component={RecommendedPathwayComponent}
        componentProps={(item, itemIndex) => {
          return getRecommendedPathwayProps(
            trackingData,
            item,
            itemIndex,
            pathways,
            role,
            index,
            s12nTargetDegrees?.[item.id],
            'subtitleMedium',
            twoColumnLayout,
            { hideCoachButton }
          );
        }}
        items={pathways}
        secondaryCta={<RecommendedRoleSecondaryCta role={role} carouselTrackingData={trackingData.carousel} />}
        itemsGridConfig={{ xs: 12, sm: 12, md: 12, lg: 12 }}
        numDisplayedRows={numDisplayedRows}
        showMoreAmount={3}
        trackingData={{ carousel: trackingData.carousel }}
      />
    </Grid>
  );
  return twoColumnLayout ? content : <PageGridContainer>{content}</PageGridContainer>;
};

export default SimpleSpotlightCollectionModuleRenderer;
